import createModule from "../createModule"
import view from "./view"
import { get, size } from "lodash"
import { generateClassificationQuery, classificationsQuery } from "helpers/classification"
import { getUnmodeledDates } from "selectors/dates"
import { getHistoricalFactorSensitivityIsAvailable, getSort, getPage, getSelectedColumns } from "./selectors"
import { getModelStartDate, getModelCurrentDate, getSelectedDate, getSelectedModelId } from "./selectors/dates"
import { getPortfoliosList, getWatchlistsList, getEtfsList, getSwapsList, getIndexesList } from "./selectors/universes"
import { getValidFilter, getEditingFilter, getClassificationsTiers, getCurrency, getFilterId, getCountryFacets, getCurrencyFacets, getClassificationFacets, getFacetedFilter, getFacetedFilterId } from "./selectors/filter"
import { Creators } from "./model"
import { BASKETS_QUERY } from "resources/baskets"
import { MODEL_METADATA_QUERY } from "resources/models"
import { CONTENT_SETS_METADATA_QUERY } from "resources/content-sets"
import "./styles.scss"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    modelId: getSelectedModelId(state, props),
    selectedDate: getSelectedDate(state, props),
    classificationsTiers: getClassificationsTiers(state, props),
  }),
  required: ["account", "accountId", "modelId", "selectedDate"],
  data: [{
    resource: "modelCountries",
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/countries`,
  }, {
    resource: "modelCurrencies",
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/currencies`,
  }, {
    resource: "etfs",
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/etfs`,
  }, {
    resource: "watchlists",
    url: () => {
      return {
        id: "watchlists",
        query: `{
          watchlists {
            id
            alias
            name
          }
        }`,
      }
    },
  }, BASKETS_QUERY, CONTENT_SETS_METADATA_QUERY, {
    resource: "classifications",
    url: () => ({
      id: "classifications",
      query: classificationsQuery,
    }),
  }, {
    resource: "classificationsValues",
    url: ({ classificationsTiers, selectedDate }) => {
      if (!classificationsTiers || size(classificationsTiers) === 0 || !selectedDate) {
        return null
      }

      // We don't know the depth of each classification, generate each classification sub query according to the tier depth
      const innerQuery = generateClassificationQuery(classificationsTiers)
      return {
        id: `classificationsValues-${selectedDate}-${JSON.stringify(classificationsTiers)}`,
        query: `query {
            ${innerQuery}
          }`,
      }
    },
  }, {
    resource: "historicalFactorSensitivityAvailability",
    url: ({ account, modelId, selectedDate }) => ({
      url: `/api/${account}/m/${modelId}/factorReturns/hasEnoughHistory`,
      id: `historicalFactorSensitivityAvailability-${selectedDate}-${modelId}-${account}`,
      payload: { selectedDate },
    }),
  }, {
    resource: "securityFinderPreferences",
    url: ({ accountId, modelId }) => {
      return ({
        id: `securityFinderPreferences-${accountId}${modelId}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            {
              type: "securityFinderSelectedColumns",
              key: `${modelId}`,
              accountId,
            },
          ],
        },
      })
    },
  },
  MODEL_METADATA_QUERY,
  ],
  actions: Creators,
  mapStateToProps: (state, props) => ({
    startDate: getModelStartDate(state, props),
    endDate: getModelCurrentDate(state, props),
    portfoliosList: getPortfoliosList(state, props),
    watchlistsList: getWatchlistsList(state, props),
    etfsList: getEtfsList(state, props),
    swapsList: getSwapsList(state, props),
    indexesList: getIndexesList(state, props),
    unmodeledDates: getUnmodeledDates(state, props),
    name: get(state, "modules.filterSaver.currentFilterName"),
    filter: getValidFilter(state, props),
    filterId: getFilterId(state, props),
    facetedFilter: getFacetedFilter(state, props),
    facetedFilterId: getFacetedFilterId(state, props),
    editingFilter: getEditingFilter(state, props),
    currentFilter: getValidFilter(state, props),
    currency: getCurrency(state, props),
    isHistoricalFactorSensitivityAvailable: getHistoricalFactorSensitivityIsAvailable(state),
    sort: getSort(state, props),
    page: getPage(state, props),
    selectedCountries: getCountryFacets(state, props),
    selectedCurrencies: getCurrencyFacets(state, props),
    selectedClassifications: getClassificationFacets(state, props),
    selectedColumns: getSelectedColumns(state, props),
  }),
})
